import { createLocation, createPath } from "history";
import { isSessionReplayStarted, matchReplayRoute, startSessionReplay } from "./session-replay";
import { POST_LOGIN_PATHS } from "../constants/postLoginPaths";
import {
  CHOICE,
  CHOICE_STORAGE_KEY,
  hasBrowserOptOutSignal,
  isOffByRegionDefault,
  isRecordingOff,
  isReplayFeatureAvailable,
  purgeRecorderTraces,
  readChoice,
  reloadToStopRecording,
  runPendingPurge,
  withdrawRecording,
  writeChoice,
} from "./replay-consent";
import { pageNavigator } from "./page-navigator";
import { getPristineFetch } from "./pristine-fetch";

/*
 * THE FENCED DOCUMENT (M3, notice plus opt-out, approved 2026-10-19).
 *
 * A running Hotjar recorder cannot be paused or unloaded, it records page
 * addresses, and masking page content does not hide an address or a link
 * target. So the invariant from M2 stands: no address a recording document
 * ever holds may carry a secret. What changed is how a recording document
 * moves. M2 sealed it (every move was a full page load); M3 FENCES it:
 *
 *   - a move between two addresses that both pass the address rule (rule 1)
 *     stays in place, and recording continues across it;
 *   - a move to any address that fails becomes a full page load
 *     (pageNavigator.assign), which is the only thing that ends a recording.
 *
 * Rule 1, the address: a route on the journey allow-list
 * (session-replay.js REPLAY_ALLOWED_ROUTES, anchored, shape-checked ids), the
 * deny list first, only that route's query keys with shape-checked values, and
 * no fragment.
 *
 * TAINT. A document that has EVER held a failing address never starts
 * recording, for the rest of its life. It is tainted by:
 *   - the address it is at when the gate first sees it (rule 1);
 *   - the address it was requested with — the navigation entry, which still
 *     holds what the app or the shell scrubbed out of the address (rule 2;
 *     missing or unreadable also taints);
 *   - a referrer with a query or a fragment (rule 3);
 *   - every router address the gate sees, and every pushState/replaceState URL
 *     argument, that fails rule 1 (watched from the gate's first run);
 *   - a surface that must never record opening in place (markReplayIneligible);
 *   - recording being off when the document loaded (a stored refusal, or the
 *     European/UK default): turning it on later, in Settings or another tab,
 *     applies from the next page load, never mid-visit (code review of #896;
 *     the Settings copy says "the next time you open QLearn").
 *
 * Rule 4, storage, is "not yet" rather than "never": while a Google sign-in
 * return is pending (oauthReturnPath / oauthPending in local storage) nothing
 * starts; once login.jsx has consumed them, the next gate run may start. A
 * signed-in session (the token) no longer blocks recording.
 *
 * Plus: the build flag, no Global Privacy Control or Do Not Track, and
 * recording not off
 * (no stored refusal; in a European or UK time zone, a stored "allowed":
 * replay-consent.js isRecordingOff).
 *
 * The fence (installed just before Hotjar starts, never removed in that
 * document):
 *   - router push/replace to a failing address becomes a full page load;
 *   - a native pushState/replaceState to a failing address is dropped;
 *   - back/forward to a failing entry reloads before the router or a recorder
 *     sees it; a page restored from the back-forward cache that no longer
 *     qualifies reloads; another tab turning recording off reloads;
 *   - the recorder's own fetches skip the app's fetch wrapper (store.js).
 *
 * The app's own native address writes go through replaceAddress(), which
 * skips a failing URL whenever this document could still record.
 */

// ---- rule 1: the address ---------------------------------------------------

// Values that look like credentials never ride a campaign tag or click id.
const SECRET_SHAPED = /eyJ|[0-9a-f]{32,}|dq[a-z]_|notes-to-quiz/i;
const CAMPAIGN_VALUE = /^[A-Za-z0-9 ._~+-]{1,100}$/;
const CLICK_ID_VALUE = /^[A-Za-z0-9_-]{8,256}$/;
const HASH_64 = /^[0-9a-f]{64}$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
// A question id is the question bank's ObjectId hex (QLearn-service
// AudioManifestEntryValidator.QUESTION_ID).
const QUESTION_ID = /^[0-9a-f]{24}$/;

const campaignValue = (v) => CAMPAIGN_VALUE.test(v) && !SECRET_SHAPED.test(v);
const clickIdValue = (v) => CLICK_ID_VALUE.test(v) && !SECRET_SHAPED.test(v);
const isoDate = (v) => {
  if (!ISO_DATE.test(v)) return false;
  const parsed = new Date(`${v}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v;
};

// Google's iOS click ids (gbraid, wbraid) are URL-safe tokens like gclid; the
// ad source and campaign id are numbers.
const GAD_SOURCE_VALUE = /^[0-9]{1,4}$/;
const GAD_CAMPAIGN_ID_VALUE = /^[0-9]{1,20}$/;
// Reddit's click id: the shape the sign-in entries keep it in the address with
// (attribution.js URL_ONLY_VALUE_SHAPES), and never secret-shaped.
const REDDIT_CLICK_ID_VALUE = /^[A-Za-z0-9_.-]{1,256}$/;

// Campaign tags and click ids (none is a secret), on the public landing pages:
// /, /register, /login (and its daily return) and the daily question.
const COMMON_KEYS = {
  utm_source: campaignValue,
  utm_medium: campaignValue,
  utm_campaign: campaignValue,
  utm_content: campaignValue,
  utm_term: campaignValue,
  gclid: clickIdValue,
  fbclid: clickIdValue,
  gbraid: clickIdValue,
  wbraid: clickIdValue,
  gad_source: (v) => GAD_SOURCE_VALUE.test(v),
  gad_campaignid: (v) => GAD_CAMPAIGN_ID_VALUE.test(v),
  rdt_cid: (v) => REDDIT_CLICK_ID_VALUE.test(v) && !SECRET_SHAPED.test(v),
};

// The daily question's public identity (no secret: the date and the
// revision's content hash), on the daily page and on its sign-in return.
const DAILY_IDENTITY = {
  date: isoDate,
  revisionId: (v) => /^dqr_[0-9a-f]{64}$/.test(v),
  revisionHash: (v) => HASH_64.test(v),
};
const DAILY_IDENTITY_KEYS = Object.keys(DAILY_IDENTITY);
const DAILY_PATH = "/daily-absite-question";

// Per route (by name, session-replay.js). Everything else — touch, grant,
// token, ref, programCode, any unknown key, and any key on a route not listed
// here — fails. Allow-listing keys, not deny-listing secrets, is the point.
const ROUTE_KEYS = {
  // The root and /register redirect in place to /login, keeping these keys.
  root: { ...COMMON_KEYS },
  register: { ...COMMON_KEYS },
  login: {
    ...COMMON_KEYS,
    auth: (v) => v === "google" || v === "phone",
    next: (v) => POST_LOGIN_PATHS.includes(v),
  },
  daily: {
    ...COMMON_KEYS,
    ...DAILY_IDENTITY,
  },
  "exam-review": {
    questionId: (v) => QUESTION_ID.test(v),
  },
};

// The daily page's sign-in return (/login?next=/daily-absite-question…,
// daily-question-opaque.js buildOpaqueDailyLoginPath) keeps the campaign keys
// and the daily identity, like the daily page itself. Its touch and grant
// still fail: that address is a full load and never records.
const DAILY_LOGIN_RETURN_KEYS = { ...ROUTE_KEYS.login, ...DAILY_IDENTITY };

const keysFor = (route, params) => {
  if (route === "login" && params.get("next") === DAILY_PATH) return DAILY_LOGIN_RETURN_KEYS;
  return ROUTE_KEYS[route] || {};
};

// All three identity keys or none, and the id names the hash.
const dailyIdentityConsistent = (params, seen) => {
  if (!DAILY_IDENTITY_KEYS.some((k) => seen.has(k))) return true;
  if (!DAILY_IDENTITY_KEYS.every((k) => seen.has(k))) return false;
  return params.get("revisionId") === `dqr_${params.get("revisionHash")}`;
};

export const passesAddressRule = (address) => {
  try {
    if (typeof address !== "string" || !address.startsWith("/") || address.startsWith("//")) return false;
    if (address.includes("#")) return false;
    // Deny-list first, then the anchored journey routes (session-replay.js).
    const route = matchReplayRoute(address);
    if (route === null) return false;

    const queryAt = address.indexOf("?");
    const params = new URLSearchParams(queryAt === -1 ? "" : address.slice(queryAt + 1));
    const rules = keysFor(route, params);
    const seen = new Set();
    for (const [key, value] of params) {
      if (seen.has(key)) return false;
      seen.add(key);
      const rule = Object.prototype.hasOwnProperty.call(rules, key) ? rules[key] : null;
      if (!rule || !value || !rule(value)) return false;
    }
    return dailyIdentityConsistent(params, seen);
  } catch {
    return false;
  }
};

// ---- rules 2 to 4: the document --------------------------------------------

const currentAddress = () => window.location.href.slice(window.location.origin.length);

// A URL as this document's address (path, query and fragment, an empty "#"
// kept), or null when it is unreadable or not on this origin.
const addressOf = (url) => {
  try {
    const parsed = new URL(String(url), window.location.href);
    if (parsed.origin !== window.location.origin) return null;
    return parsed.href.slice(parsed.origin.length);
  } catch {
    return null;
  }
};

// The navigation entry's name is the address the document was requested with,
// before any script rewrote it. Missing, unreadable or cross-origin: null.
const navigationEntryAddress = () => {
  try {
    const entries = window.performance && typeof window.performance.getEntriesByType === "function"
      ? window.performance.getEntriesByType("navigation")
      : null;
    const name = entries && entries[0] && entries[0].name;
    const origin = window.location.origin;
    if (typeof name !== "string" || !name.startsWith(`${origin}/`)) return null;
    return name.slice(origin.length);
  } catch {
    return null;
  }
};

const passesReferrerRule = () => {
  try {
    const referrer = document.referrer || "";
    return !referrer.includes("?") && !referrer.includes("#");
  } catch {
    return false;
  }
};

// "Not yet": a Google round trip is coming back to /login and will push its
// stashed return path next. login.jsx consumes both keys (its mount effect
// removes oauthPending; goToPostLoginPath takes oauthReturnPath); after that
// the gate's next run may start. A storage error counts as "not yet".
const PENDING_SIGN_IN_KEYS = ["oauthReturnPath", "oauthPending"];

export const passesStorageRule = () => {
  try {
    return PENDING_SIGN_IN_KEYS.every((key) => !window.localStorage.getItem(key));
  } catch (e) {
    return false;
  }
};

let tainted = false;
let taintReason = null;
let loadClassified = false;
const documentListeners = new Set();

// Hears the fence going on, the recorder starting and the document becoming
// tainted, so the daily page and the Settings switch re-render.
export const subscribeDocument = (fn) => {
  documentListeners.add(fn);
  return () => documentListeners.delete(fn);
};

const notifyDocumentListeners = () => {
  documentListeners.forEach((fn) => {
    try { fn(); } catch (e) { /* keep notifying */ }
  });
};

const taint = (reason, { quiet = false } = {}) => {
  if (tainted) return;
  tainted = true;
  taintReason = reason;
  if (!quiet) notifyDocumentListeners();
};

// Rules 1 to 3 for the load itself, once per document, at its first use, plus
// the recording choice as it stood at load. Reads only (the address, the
// navigation entry, the referrer, the stored choice). Quiet: it can run during
// a render, and every reader classifies first, so there is nobody stale to
// tell.
const ensureLoadClassified = () => {
  if (loadClassified) return;
  loadClassified = true;
  const quiet = { quiet: true };
  try {
    if (isReplayFeatureAvailable() && isRecordingOff()) { taint("off-at-load", quiet); return; }
    if (!passesAddressRule(currentAddress())) { taint("address", quiet); return; }
    const original = navigationEntryAddress();
    if (original === null || !passesAddressRule(original)) { taint("original-address", quiet); return; }
    if (!passesReferrerRule()) taint("referrer", quiet);
  } catch {
    taint("error", quiet);
  }
};

export const isDocumentTainted = () => {
  ensureLoadClassified();
  return tainted;
};

// Why, for tests and debugging only.
export const documentTaintReason = () => {
  ensureLoadClassified();
  return taintReason;
};

// The router's addresses in this document. Any that fails rule 1 taints it.
export const noteRouterAddress = (address) => {
  ensureLoadClassified();
  if (!passesAddressRule(address)) taint("router-address");
};

// A surface that must never be recorded opened in place on this document (the
// institutional / email-code sign-in: it shows the typed address back, stores
// the session token and asks for a name, all on /login). Like a failing
// address, it taints the document: a later "Turn on" applies from the next
// load. On a recording page login.jsx leaves by a full page load instead.
export const markReplayIneligible = () => {
  if (fenced || !isReplayFeatureAvailable()) return;
  taint("ineligible");
};

// Whether this document could still record: the feature is built in, no GPC
// or Do Not Track, recording not off, and no taint. Before the gate's first run the load is
// classified on demand. The daily page and replaceAddress() read this.
export const documentCouldRecord = () => {
  try {
    if (!isReplayFeatureAvailable()) return false;
    if (fenced) return true;
    if (hasBrowserOptOutSignal()) return false;
    if (isRecordingOff()) return false;
    return !isDocumentTainted();
  } catch {
    // Unknown: behave as if it could (skip hazardous writes).
    return true;
  }
};

// The address is re-checked now, as is the pending Google return.
export const canStartRecordingNow = () => {
  try {
    // Includes: never in the native app or behind the third-party tracking
    // kill switch (replay-consent.js isRecordingBlockedOnThisRuntime).
    if (!isReplayFeatureAvailable()) return false;
    if (hasBrowserOptOutSignal()) return false;
    if (isRecordingOff()) return false;
    if (isDocumentTainted()) return false;
    if (!passesAddressRule(currentAddress())) return false;
    return passesStorageRule();
  } catch {
    return false;
  }
};

// ---- the history watch (from the gate's first run) -------------------------

let fenced = false;
let teardown = [];
let pendingStart = null;
let historyWatchInstalled = false;
let wrappedRouterHistories = new WeakSet();

export const isFenced = () => fenced;

// Every native address write. While the document is not recording, a write to
// a failing address taints it (it goes through: the page behaves as before).
// While recording, such a write is dropped. State-only writes pass untouched.
const installHistoryWatch = () => {
  if (historyWatchInstalled) return;
  historyWatchInstalled = true;
  const { history } = window;
  ["pushState", "replaceState"].forEach((method) => {
    const own = Object.prototype.hasOwnProperty.call(history, method) ? history[method] : undefined;
    const inner = history[method];
    history[method] = function watchedHistoryWrite(...args) {
      const url = args[2];
      if (url === undefined || url === null) return inner.apply(history, args);
      const address = addressOf(url);
      if (address === null || !passesAddressRule(address)) {
        if (fenced) return undefined; // dropped
        taint("history-write");
      }
      return inner.apply(history, args);
    };
    teardown.push(() => {
      if (own) history[method] = own;
      else delete history[method];
    });
  });
};

// ---- the fence -------------------------------------------------------------

// Where a router push/replace would go: the router's own resolution (relative
// paths, location objects, the basename), as an absolute URL.
const routerTargetHref = (routerHistory, to, state) => {
  try {
    const location = createLocation(to, state, undefined, routerHistory.location);
    const path = typeof routerHistory.createHref === "function"
      ? routerHistory.createHref(location)
      : createPath(location);
    return new URL(path, window.location.href).href;
  } catch {
    return null;
  }
};

const routerTargetState = (routerHistory, to, state) => {
  try {
    return createLocation(to, state, undefined, routerHistory.location).state;
  } catch (e) {
    return undefined;
  }
};

const resolveHref = (to) => {
  try {
    if (typeof to === "string") return new URL(to, window.location.href).href;
    if (to && typeof to === "object") return new URL(createPath(createLocation(to)), window.location.href).href;
    return null;
  } catch {
    return null;
  }
};

// Leave the page by a full load (the institutional tab, the daily unlock).
export const leaveRecordingPage = (path) => {
  const href = resolveHref(path);
  if (href) pageNavigator.assign(href);
};

// A router move can carry location.state (Audio Mode's quiz settings to
// /drive, the next-practice state to the builder). A full page load loses it,
// so when the fence turns a move with state into a full load, the state rides
// along in session storage and is put back into the new document's history
// entry before its router reads it (restoreCarriedRouterState, at module
// evaluation). Review of #866, blocker 3: Audio Mode opened /drive empty.
export const CARRIED_STATE_KEY = "qlearn_fence_router_state";
const CARRIED_STATE_MAX_AGE_MS = 60 * 1000;

// Path and query, without a trailing slash: the host may answer /drive with a
// redirect to /drive/.
const carriedTarget = (href) => {
  try {
    const u = new URL(href, window.location.href);
    return `${u.pathname.replace(/\/+$/, "") || "/"}${u.search}`;
  } catch {
    return null;
  }
};

const carryRouterState = (href, state) => {
  if (state === undefined || state === null) return;
  try {
    window.sessionStorage.setItem(CARRIED_STATE_KEY, JSON.stringify({ target: carriedTarget(href), state, at: Date.now() }));
  } catch (e) {
    // Unserializable state or blocked storage: the move still happens.
  }
};

// Called once as this module loads, before <Router> creates its history.
// history v4 reads the initial location.state from window.history.state.state;
// this is a state-only write (no URL), so the address never changes. No entry
// key is made up: history v4 takes an entry without one, as on any fresh page
// load.
export const restoreCarriedRouterState = () => {
  try {
    if (!isReplayFeatureAvailable()) return false;
    const raw = window.sessionStorage.getItem(CARRIED_STATE_KEY);
    if (!raw) return false;
    window.sessionStorage.removeItem(CARRIED_STATE_KEY);
    const carried = JSON.parse(raw);
    if (!carried || carried.target !== carriedTarget(window.location.href)) return false;
    if (!(Date.now() - Number(carried.at) < CARRIED_STATE_MAX_AGE_MS)) return false;
    const current = window.history.state && typeof window.history.state === "object" ? window.history.state : {};
    window.history.replaceState({ ...current, state: carried.state }, "");
    return true;
  } catch {
    return false;
  }
};

const wrapRouterHistory = (routerHistory) => {
  if (!routerHistory || typeof routerHistory.push !== "function" || typeof routerHistory.replace !== "function") {
    return;
  }
  if (wrappedRouterHistories.has(routerHistory)) return;
  wrappedRouterHistories.add(routerHistory);
  const originalPush = routerHistory.push;
  const originalReplace = routerHistory.replace;
  const fencedNavigation = (original) => function navigateFromRecordingPage(to, state) {
    const href = routerTargetHref(routerHistory, to, state);
    if (href === null) return undefined; // unresolvable: stay put, never move in place
    const address = addressOf(href);
    if (address !== null && passesAddressRule(address)) {
      return original.call(routerHistory, to, state); // on the journey: in place
    }
    carryRouterState(href, routerTargetState(routerHistory, to, state));
    pageNavigator.assign(href);
    return undefined;
  };
  routerHistory.push = fencedNavigation(originalPush);
  routerHistory.replace = fencedNavigation(originalReplace);
  teardown.push(() => {
    routerHistory.push = originalPush;
    routerHistory.replace = originalReplace;
  });
};

// Hotjar (part of Contentsquare) hosts. Their fetches go out through the
// browser's own fetch: store.js's wrapper adds `Authorization: Bearer <token>`
// to requests for the QLearn API, and recording now runs on signed-in pages.
// Every other request keeps going through the app's wrapper unchanged.
const RECORDER_HOST = /(^|\.)(hotjar\.com|hotjar\.io|contentsquare\.com|contentsquare\.net)$/i;

const requestHost = (input) => {
  try {
    const raw = typeof input === "string" ? input : (input && (input.url || input.href)) || String(input);
    return new URL(raw, window.location.href).hostname;
  } catch {
    return "";
  }
};

const routeRecorderFetchPastAppWrapper = () => {
  const pristine = getPristineFetch();
  const appFetch = window.fetch;
  if (typeof pristine !== "function" || typeof appFetch !== "function" || pristine === appFetch) return;
  window.fetch = function recordingPageFetch(input, init) {
    if (RECORDER_HOST.test(requestHost(input))) return pristine.call(window, input, init);
    return appFetch.apply(this, arguments);
  };
  teardown.push(() => {
    window.fetch = appFetch;
  });
};

// Capture phase on window runs before the router's own popstate listener, so
// the app never renders a failing entry and later listeners (a recorder's
// included) never see the event. A passing entry stays in place.
const onPopState = (event) => {
  if (!fenced || passesAddressRule(currentAddress())) return;
  event.stopImmediatePropagation();
  pageNavigator.reload();
};

const stillQualifies = () => isReplayFeatureAvailable()
  && !hasBrowserOptOutSignal()
  && !isRecordingOff()
  && passesAddressRule(currentAddress());

const onPageShow = (event) => {
  if (!fenced || !event || !event.persisted) return;
  if (stillQualifies()) return;
  // Turned off elsewhere while this page sat in the back-forward cache: stop
  // it the way Turn off does (purge, then reload), not with a bare reload.
  if (readChoice() === CHOICE.DECLINED) reloadToStopRecording();
  else pageNavigator.reload();
};

// "Turn off" in another tab: this recording page stops too (a reload is the
// only way), and purges what the recorder wrote meanwhile.
const onStorage = (event) => {
  if (!fenced || !event) return;
  if (event.key !== null && event.key !== CHOICE_STORAGE_KEY) return;
  if (readChoice() === CHOICE.DECLINED) reloadToStopRecording();
};

export const fenceDocument = (routerHistory) => {
  installHistoryWatch();
  if (routerHistory) wrapRouterHistory(routerHistory);
  if (fenced) return;
  fenced = true;
  routeRecorderFetchPastAppWrapper();
  window.addEventListener("popstate", onPopState, true);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("storage", onStorage);
  teardown.push(() => {
    window.removeEventListener("popstate", onPopState, true);
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("storage", onStorage);
  });
  notifyDocumentListeners();
};

// The app's own native address writes. A failing URL is skipped while this
// document could still record (the in-memory/session state the caller also
// keeps carries on as before); otherwise it is written as before, and a
// failing one taints the document.
export const replaceAddress = (state, url, historyObject = window.history) => {
  if (isReplayFeatureAvailable()) {
    const address = addressOf(url);
    const passes = address !== null && passesAddressRule(address);
    if (!passes) {
      if (documentCouldRecord()) return false;
      taint("address-write");
    }
  }
  historyObject.replaceState(state, "", url);
  return true;
};

// ---- starting --------------------------------------------------------------

// Fence first, then (masking, then) Hotjar. `defer` lets the click that turned
// recording on commit its re-render before the recorder takes its first
// snapshot.
export const maybeStartRecording = ({ routerHistory, defer = false } = {}) => {
  try {
    if (!canStartRecordingNow()) return false;
    fenceDocument(routerHistory);
    const start = () => {
      const before = isSessionReplayStarted();
      const running = startSessionReplay();
      if (running && !before) notifyDocumentListeners();
      return running;
    };
    if (!defer) return start();
    if (!pendingStart) {
      pendingStart = setTimeout(() => {
        pendingStart = null;
        if (fenced && canStartRecordingNow()) start();
      }, 0);
    }
    return true;
  } catch {
    return false;
  }
};

// ---- the Settings switch ---------------------------------------------------
//
// Whether a recorder runs in THIS document (a fenced page started it).
export const isRecordingThisDocument = () => fenced || isSessionReplayStarted();

// Off: store the refusal (kept in this browser, survives sign-out) and remove
// Hotjar's and Contentsquare's cookies and storage. Settings is never
// recorded, so there the purge is all it takes; a page whose recorder is
// running reloads to stop it (and purges again after the reload), exactly as
// "Turn off" always did. Any other open tab that is recording hears the
// stored refusal and reloads itself (onStorage above). Returns true when this
// page is reloading.
export const turnRecordingOff = () => {
  if (isRecordingThisDocument()) {
    withdrawRecording();
    return true;
  }
  writeChoice(CHOICE.DECLINED);
  purgeRecorderTraces();
  return false;
};

// On: store "allowed". Recording may start on the next recorded page that
// qualifies; a document that has held an address off the journey (Settings
// always has) never starts, so from Settings it begins the next time QLearn
// is opened. Never starts anything on its own where the start rule says no.
export const turnRecordingOn = ({ routerHistory } = {}) => {
  writeChoice(CHOICE.ALLOWED);
  return maybeStartRecording({ routerHistory, defer: true });
};

let firstRunDone = false;

// The gate calls this on mount and on every navigation, after the route's
// body suppression.
export const syncReplayDocument = (address, routerHistory) => {
  try {
    if (!isReplayFeatureAvailable()) return false;
    if (!firstRunDone) {
      firstRunDone = true;
      ensureLoadClassified();
      try { installHistoryWatch(); } catch (e) { taint("watch"); }
      runPendingPurge();
      // A browser that sends GPC or Do Not Track, or a European/UK visitor
      // with nothing stored, never records, so anything Hotjar or
      // Contentsquare left from before (an earlier visit) is removed too, once
      // per page load, with the same purge as turning recording off. Nothing
      // records here, so no reload.
      if (hasBrowserOptOutSignal() || isOffByRegionDefault()) purgeRecorderTraces();
    }
    noteRouterAddress(address);
    if (fenced && !passesAddressRule(address)) {
      // Unreachable through the router wrapper; if the router got here some
      // other way, leave rather than render a failing address in place.
      leaveRecordingPage(address);
      return false;
    }
    return maybeStartRecording({ routerHistory });
  } catch {
    return false;
  }
};

// Before <Router> reads its first location (see restoreCarriedRouterState).
restoreCarriedRouterState();

// Test seam only. Production code never unfences a document.
export const __resetReplayDocument = () => {
  teardown.reverse().forEach((fn) => {
    try { fn(); } catch (e) { /* best effort */ }
  });
  teardown = [];
  if (pendingStart) clearTimeout(pendingStart);
  pendingStart = null;
  fenced = false;
  tainted = false;
  taintReason = null;
  loadClassified = false;
  historyWatchInstalled = false;
  wrappedRouterHistories = new WeakSet();
  firstRunDone = false;
};
